import React, { useState, useEffect, useCallback } from 'react';
import {
    Save, Loader2, FolderOpen, FilePlus, Share2, CircleHelp, ShieldAlert, Globe
} from 'lucide-react';
import SwornUILayout from './SwornUILayout';
import { FirebaseProvider, useFirebase } from './contexts/firebaseContext';
import { SwornBuilderProvider, useSwornBuilderContext } from './contexts/SwornBuilderContext';
import { SWORN_DATA } from './gameData';
import { KnightKey, ContextMenuData } from './types';
import ContextMenu from './components/ui/ContextMenu';
import SaveBuildModal from './components/modals/SaveBuildModal';
import LoadBuildModal from './components/modals/LoadBuildModal';
import KnightSelectorModal from './components/modals/KnightSelectorModal';
import LoadoutModal from './components/modals/LoadoutModal';
import CompendiumKnightsModal from './components/modals/CompendiumKnightsModal';
import CompendiumFaeModal from './components/modals/CompendiumFaeModal';
import CommunityBuildsModal from './components/modals/CommunityBuildsModal';
import ShareBuildModal from './components/modals/ShareBuildModal';
import ConfirmModal from './components/modals/ConfirmModal';
import AdminPanel from './components/panels/AdminPanel';
import { useAppModals } from './hooks/modules/useAppModals';
import AssetPreloader from './components/helpers/AssetPreloader';
import { BuildData } from './types';

export default function App() {
  return (
    <FirebaseProvider>
      <SwornBuilderProvider>
        <AssetPreloader />
        <AppContent />
      </SwornBuilderProvider>
    </FirebaseProvider>
  );
}

function AppContent() {
  const { isAuthReady, useLocalStorage: isLocalMode, userId } = useFirebase();
  const {
    selectedKnight, setSelectedKnight,
    selectedWeapon, setSelectedWeapon,
    selectedSpell, setSelectedSpell,
    selectedBlessings, toggleBlessing,
    savedBuilds,
    currentBuildId,
    currentBuildName,
    saveMessage, setSaveMessage,
    viewedBlessing, setViewedBlessing,
    handleSaveBuild,
    handleLoadBuild,
    handleDeleteBuild,
    handleNewBuild,
    handleShareBuild,
    updateBuild,
    saveBuild,
    contextMenu,
    closeContextMenu,
    containerRef,
    selectedCompendiumKnight,
    selectedCompendiumFae
  } = useSwornBuilderContext();

  const [showSplash, setShowSplash] = useState(() => {
    // Don't show splash if we are loading a shared build
    const params = new URLSearchParams(window.location.search);
    return !params.has('build');
  });

  const {
    showSaveModal, setShowSaveModal,
    showLoadModal, setShowLoadModal,
    showKnightModal, setShowKnightModal,
    showLoadoutModal, setShowLoadoutModal,
    showCompendiumKnightModal, setShowCompendiumKnightModal,
    showCompendiumFaeModal, setShowCompendiumFaeModal,
    showCommunityModal, setShowCommunityModal,
    saveMode, setSaveMode,
    
    showShareModal, setShowShareModal,
    shareLink, setShareLink,
    showShareConfirmModal, setShowShareConfirmModal,
    pendingShare, setPendingShare,

    showTutorial, setShowTutorial,
    handleCloseTutorial,

    showAdminPanel, setShowAdminPanel,
    showAdminLogin, setShowAdminLogin,
    adminPasswordInput, setAdminPasswordInput,
    handleAdminLogin
  } = useAppModals(userId, showSplash);

  const splashUrl = SWORN_DATA.ui_assets?.splash_screen || '/assets/uielements/web_background.png';


  const handleSaveButtonClick = useCallback(() => {
    if (!selectedKnight || !selectedWeapon || !selectedSpell) {
      setSaveMessage('Please select a knight, weapon, and spell first!');
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }
    if (currentBuildId) {
      handleSaveBuild(currentBuildName || 'Unnamed Build', false);
    } else {
      setSaveMode('new');
      setShowSaveModal(true);
    }
  }, [selectedKnight, selectedWeapon, selectedSpell, setSaveMessage, currentBuildId, currentBuildName, handleSaveBuild]);

  const onModifierSlotClick = useCallback((slot: number) => {
    setSaveMessage(`Modifier slot ${slot} clicked`);
    setTimeout(() => setSaveMessage(null), 3000);
  }, [setSaveMessage]);

  const handleShareButtonClick = useCallback(() => {
    if (!selectedKnight || !selectedWeapon || !selectedSpell) {
      setSaveMessage('Please select a knight, weapon, and spell first!');
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }
    
    if (currentBuildId) {
        setShowShareConfirmModal(true);
    } else {
        setPendingShare(true);
        setSaveMode('new');
        setShowSaveModal(true);
    }
  }, [selectedKnight, selectedWeapon, selectedSpell, currentBuildId, setSaveMessage]);

  const executeShare = async () => {
      const shareId = await handleShareBuild();
      if (shareId) {
          const link = `${window.location.origin}${window.location.pathname}?build=${shareId}`;
          setShareLink(link);
          setShowShareModal(true);
      }
  };

  const handleConfirmShare = async () => {
      await handleSaveBuild(currentBuildName || 'Unnamed Build', false);
      setShowShareConfirmModal(false);
      await executeShare();
  };

  const handleUpdateBuild = useCallback(async (id: string, updates: Partial<BuildData>) => {
    await updateBuild(id, updates);
  }, [updateBuild]);

  const handleExportBuild = useCallback((build: BuildData) => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(build));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `${build.name.replace(/\s+/g, '_')}.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  }, []);

  const handleImportBuild = useCallback(async (buildData: Omit<BuildData, 'id' | 'createdAt' | 'updatedAt'>) => {
    await saveBuild(buildData);
    setSaveMessage(`Imported "${buildData.name}"`);
    setTimeout(() => setSaveMessage(null), 3000);
  }, [saveBuild, setSaveMessage]);

  const handleDuplicateBuild = useCallback(async (build: BuildData) => {
    const { id, createdAt, updatedAt, ...buildData } = build;
    const newName = `${buildData.name} (Copy)`;
    await saveBuild({ ...buildData, name: newName });
    setSaveMessage(`Duplicated "${buildData.name}"`);
    setTimeout(() => setSaveMessage(null), 3000);
  }, [saveBuild, setSaveMessage]);

  const handleContextAction = useCallback((type: ContextMenuData['type'], action: string, target?: string) => {
    switch (type) {
      case 'knight':
        setSelectedKnight(action.toLowerCase() as KnightKey);
        setSaveMessage(`Selected ${action}`);
        break;
      case 'weapon':
        setSelectedWeapon(action);
        setSaveMessage(`Selected ${action.split('_').join(' ')}`);
        break;
      case 'spell':
        setSelectedSpell(action);
        setSaveMessage(`Selected ${action.split('_').join(' ')}`);
        break;
      case 'blessing':
        if (action === 'add' || action === 'remove') {
          toggleBlessing(target!);
        } else if (action === 'view') {
          setViewedBlessing(target || null)
        }
        break;
      case 'modifier':
        if (action === 'info') {
          onModifierSlotClick(parseInt(target!));
        }
        break;
      case 'build':
        if (action === 'Rename') {
          setSaveMode('rename');
          setShowSaveModal(true);
        } else if (action === 'Save As') {
          setSaveMode('new');
          setShowSaveModal(true);
        } else if (action === 'Save') {
          handleSaveButtonClick();
        } else if (action === 'Load') {
          setShowLoadModal(true);
        } else if (action === 'New') {
          handleNewBuild();
        }
        break;
    }
    closeContextMenu();
    setTimeout(() => setSaveMessage(null), 2000);
  }, [closeContextMenu, setSelectedKnight, setSelectedSpell, setSelectedWeapon, setViewedBlessing, toggleBlessing, setSaveMessage, handleSaveButtonClick, handleNewBuild, onModifierSlotClick]);

  if (!isAuthReady) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#1a1a1a] text-white">
            <p className="text-xl font-semibold flex items-center gap-3">
                <Loader2 className='w-6 h-6 animate-spin text-sworn-gold'/> Initializing Arthur's Grimoire...
            </p>
        </div>
    );
  }

  if (showSplash) {
    return (
      <div 
        onClick={() => setShowSplash(false)}
        className="fixed inset-0 z-50 cursor-pointer flex items-center justify-center bg-black"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'black',
          backgroundImage: `url(${splashUrl})`,
          backgroundSize: '75% 90%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 50
        }}
      >
        <div 
          className="animate-pulse"
          style={{
            position: 'absolute',
            bottom: '40px',
            width: '100%',
            textAlign: 'center',
            color: 'red',
            fontSize: '32px',
            fontWeight: 'bold'
          }}
        >
          Click anywhere to enter
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ backgroundColor: '#1a1a1a', minHeight: '100vh', position: 'relative' }}>
      {/* Vertical Toolbar */}
      <div 
        className="fixed bottom-4 left-1/2 -translate-x-1/2 flex flex-row md:flex-col md:top-6 md:right-2 md:left-auto md:translate-x-0 md:bottom-auto gap-2 z-[100] p-2 bg-black/60 backdrop-blur-md rounded-2xl border border-white/10 shadow-2xl"
        style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
      >
          <button onClick={handleNewBuild} title="New Build" className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-3 rounded-lg transition-colors flex items-center gap-2 justify-center md:justify-start shadow-lg border border-slate-600 w-12 xl:w-32">
            <FilePlus className="w-5 h-5" /> <span className="hidden xl:inline">New</span>
          </button>
          <button onClick={() => setShowLoadModal(true)} title="Load Build" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-3 rounded-lg transition-colors flex items-center gap-2 justify-center md:justify-start shadow-lg border border-blue-500 w-12 xl:w-32">
            <FolderOpen className="w-5 h-5" /> <span className="hidden xl:inline">Load</span>
          </button>
          <button onClick={() => setShowCommunityModal(true)} title="Community Builds" className="bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-3 rounded-lg transition-colors flex items-center gap-2 justify-center md:justify-start shadow-lg border border-teal-500 w-12 xl:w-32">
            <Globe className="w-5 h-5" /> <span className="hidden xl:inline">Explore</span>
          </button>
          <button onClick={handleSaveButtonClick} title="Save Build" className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-3 rounded-lg transition-colors flex items-center gap-2 justify-center md:justify-start shadow-lg border border-green-500 w-12 xl:w-32">
            <Save className="w-5 h-5" /> <span className="hidden xl:inline">{currentBuildId ? 'Update' : 'Save'}</span>
          </button>
          <button onClick={handleShareButtonClick} title="Share Build" className="bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2 px-3 rounded-lg transition-colors flex items-center gap-2 justify-center md:justify-start shadow-lg border border-amber-500 w-12 xl:w-32">
            <Share2 className="w-5 h-5" /> <span className="hidden xl:inline">Share</span>
          </button>
          <button onClick={() => setShowTutorial(true)} title="Tutorial" className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-3 rounded-lg transition-colors flex items-center gap-2 justify-center md:justify-start shadow-lg border border-indigo-500 w-12 xl:w-32">
            <CircleHelp className="w-5 h-5" /> <span className="hidden xl:inline">Tutorial</span>
          </button>
          <div title={isLocalMode ? 'Local Save Only' : 'Cloud Saves'} className={`px-3 py-2 rounded-lg text-xs font-bold text-center shadow-lg border w-12 xl:w-32 flex items-center justify-center ${isLocalMode ? 'bg-amber-600 border-amber-500 text-white' : 'bg-purple-600 border-purple-500 text-white'}`}>
            <span className="hidden xl:inline text-sm">{isLocalMode ? 'Local Only' : 'Cloud Saves'}</span>
            <span className="xl:hidden"><ShieldAlert className="w-5 h-5" /></span>
          </div>
      </div>

      <div className="h-[100dvh] flex items-center justify-center pb-[env(safe-area-inset-bottom)] md:pt-12">
        <SwornUILayout showTutorial={showTutorial} onCloseTutorial={handleCloseTutorial} />
      </div>

      <ContextMenu
        contextMenu={contextMenu}
        closeContextMenu={closeContextMenu}
        handleContextAction={handleContextAction}
      />

      <SaveBuildModal
        isOpen={showSaveModal}
        onClose={() => {
            setShowSaveModal(false);
            setPendingShare(false);
        }}
        onSave={async (name) => {
            await handleSaveBuild(name, saveMode === 'new');
            setShowSaveModal(false);
            if (pendingShare) {
                setPendingShare(false);
                await executeShare();
            }
        }}
        currentBuildName={saveMode === 'new' ? undefined : (currentBuildName ?? undefined)}
      />

      <ShareBuildModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareLink={shareLink}
      />

      <ConfirmModal
        isOpen={showShareConfirmModal}
        onClose={() => setShowShareConfirmModal(false)}
        onConfirm={handleConfirmShare}
        title="Save & Share"
        message="Sharing this build will save any unsaved changes. Continue?"
        confirmLabel="Save & Share"
        confirmIcon={<Share2 className="w-5 h-5" />}
        confirmButtonClass="bg-amber-600 hover:bg-amber-700"
      />

      <LoadBuildModal
        isOpen={showLoadModal}
        onClose={() => setShowLoadModal(false)}
        builds={savedBuilds}
        onLoadBuild={handleLoadBuild}
        onDeleteBuild={handleDeleteBuild}
        currentBuildId={currentBuildId}
        onUpdateBuild={handleUpdateBuild}
        onExportBuild={handleExportBuild}
        onImportBuild={handleImportBuild}
        onDuplicateBuild={handleDuplicateBuild}
      />

      <KnightSelectorModal
        isOpen={showKnightModal}
        onClose={() => setShowKnightModal(false)}
        selectedKnight={selectedKnight}
        setSelectedKnight={setSelectedKnight}
      />

      <LoadoutModal
        isOpen={showLoadoutModal}
        onClose={() => setShowLoadoutModal(false)}
        selectedKnight={selectedKnight}
        selectedWeapon={selectedWeapon}
        setSelectedWeapon={setSelectedWeapon}
        selectedSpell={selectedSpell}
        setSelectedSpell={setSelectedSpell}
      />

      <CompendiumKnightsModal
        isOpen={showCompendiumKnightModal}
        onClose={() => setShowCompendiumKnightModal(false)}
        initialKnight={selectedCompendiumKnight}
      />
      <CompendiumFaeModal
        isOpen={showCompendiumFaeModal}
        onClose={() => setShowCompendiumFaeModal(false)}
        initialFae={selectedCompendiumFae}
      />

      <CommunityBuildsModal
        isOpen={showCommunityModal}
        onClose={() => setShowCommunityModal(false)}
        onLoadBuild={handleLoadBuild}
      />

      {saveMessage && (
        <div className="fixed bottom-4 right-4 bg-purple-600 text-white px-6 py-3 rounded-lg shadow-xl border border-purple-400 z-50 animate-pulse">
          <p className="font-semibold">{saveMessage}</p>
        </div>
      )}

      {/* Admin Login Modal (Simple) */}
      {showAdminLogin && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90">
             <div className="bg-slate-800 p-6 rounded-lg border border-red-500/50 shadow-2xl w-80">
                <div className="flex items-center gap-2 mb-4 text-red-500 font-bold border-b border-red-900 pb-2">
                    <ShieldAlert size={20} />
                    <span>Restricted Access</span>
                </div>
                <input 
                    type="password" 
                    className="w-full bg-black text-white p-2 mb-4 border border-gray-700 rounded focus:border-red-500 outline-none"
                    placeholder="Enter Passcode..."
                    value={adminPasswordInput}
                    onChange={e => setAdminPasswordInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAdminLogin()}
                />
                <div className="flex justify-end gap-2">
                    <button onClick={() => setShowAdminLogin(false)} className="text-gray-400 hover:text-white px-3 py-1">Cancel</button>
                    <button onClick={handleAdminLogin} className="bg-red-600 hover:bg-red-700 text-white px-4 py-1 rounded font-bold">Access</button>
                </div>
             </div>
        </div>
      )}

      {/* Admin Panel */}
      {showAdminPanel && <AdminPanel onClose={() => setShowAdminPanel(false)} />}
    </div>
  );
}
